// src/pages/SignupPage.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  User,
  Building2,
  Bed,
  Users,
  AlertCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  MapPin,
  X,
} from 'lucide-react';
import dvsLogo from '../assets/branding/dva_logo.png';
import {
  checkClientAdminEmailExists,
  registerClientAndUser,
} from '../services/sessionsApiService';

// US States for service area selection
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function SignupPage() {
  // Form steps
  const [currentStep, setCurrentStep] = useState(1);

  // Client data (Step 1)
  const [clientData, setClientData] = useState({
    client_name: '',
    client_type: 'health_system',
    beds: '',
    lives_under_vbc_agreements: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
    },
    service_area_states: [],
  });

  // User data (Step 2)
  const [userData, setUserData] = useState({
    name_first: '',
    name_last: '',
    email: '',
    role: 'CLIENT_ADMIN',
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Client type options
  const clientTypeOptions = [
    { value: 'collaboration', label: 'Collaboration' },
    { value: 'consultant', label: 'Consultant' },
    { value: 'health_system', label: 'Health System' },
    { value: 'hospital', label: 'Hospital' },
    { value: 'vendor', label: 'Vendor' },
  ];

  // Validate Step 1 - Check if all required fields are filled
  const isStep1Valid = () => {
    return (
      clientData.client_name.trim() !== '' &&
      clientData.address.street.trim() !== '' &&
      clientData.address.city.trim() !== '' &&
      clientData.address.state.trim() !== '' &&
      clientData.address.zip.trim() !== '' &&
      clientData.service_area_states.length > 0
    );
  };

  // Validate Step 2 - Check if all required fields are filled
  const isStep2Valid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return (
      userData.name_first.trim() !== '' &&
      userData.name_last.trim() !== '' &&
      userData.email.trim() !== '' &&
      emailRegex.test(userData.email)
    );
  };

  // Validate Step 1 with error messages
  const validateStep1 = () => {
    if (!clientData.client_name.trim()) {
      setError('Please enter your organization name');
      return false;
    }
    if (!clientData.address.street.trim()) {
      setError('Please enter street address');
      return false;
    }
    if (!clientData.address.city.trim()) {
      setError('Please enter city');
      return false;
    }
    if (!clientData.address.state.trim()) {
      setError('Please select state');
      return false;
    }
    if (!clientData.address.zip.trim()) {
      setError('Please enter ZIP code');
      return false;
    }
    if (clientData.service_area_states.length === 0) {
      setError('Please select at least one service area state');
      return false;
    }
    const bedsNum = Number(clientData.beds);
    if (clientData.beds !== '' && (isNaN(bedsNum) || bedsNum < 0)) {
      setError('Number of beds must be a valid non-negative number');
      return false;
    }
    const livesNum = Number(clientData.lives_under_vbc_agreements);
    if (clientData.lives_under_vbc_agreements !== '' && (isNaN(livesNum) || livesNum < 0)) {
      setError('Lives under VBC agreements must be a valid non-negative number');
      return false;
    }
    return true;
  };

  // Validate Step 2 with error messages
  const validateStep2 = () => {
    if (!userData.name_first.trim()) {
      setError('Please enter your first name');
      return false;
    }
    if (!userData.name_last.trim()) {
      setError('Please enter your last name');
      return false;
    }
    if (!userData.email.trim()) {
      setError('Please enter your email address');
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  // Handle Step 1 next button
  const handleNextStep = (e) => {
    e.preventDefault();
    setError('');

    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  // Handle Step 2 back button
  const handlePrevStep = () => {
    setError('');
    setCurrentStep(1);
  };

  // Handle final submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateStep2()) {
      return;
    }

    try {
      setLoading(true);

      const emailExists = await checkClientAdminEmailExists(userData.email.trim());

      if (emailExists) {
        setError('An account with this email already exists. Please use a different email or sign in.');
        return;
      }

      // Convert number fields from strings to numbers for API
      const clientDataForSubmit = {
        ...clientData,
        beds: clientData.beds === '' ? 0 : Number(clientData.beds),
        lives_under_vbc_agreements: clientData.lives_under_vbc_agreements === '' ? 0 : Number(clientData.lives_under_vbc_agreements),
      };

      const result = await registerClientAndUser({
        client: clientDataForSubmit,
        user: userData,
      });

      console.log('Registration successful:', result);
      setSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      setError(
        err.message || 'Failed to create account. Please try again or contact support.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>

              <h2
                className="text-2xl mb-3"
                style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
              >
                Account Created Successfully!
              </h2>

              <p className="text-[#717182] mb-6" style={{ fontWeight: 500 }}>
                We've sent a verification email to <strong>{userData.email}</strong>. Please check
                your inbox and click the verification link to activate your account.
              </p>

              <div className="space-y-3">
                <Link
                  to="/login"
                  className="block w-full px-6 py-3 bg-gradient-to-r from-[#18365E] to-[#18365E]/90 text-white rounded-lg hover:shadow-lg transition-all text-center"
                  style={{ fontWeight: 600 }}
                >
                  Go to Login
                </Link>

                <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
                  Didn't receive the email?{' '}
                  <a href="#" className="text-[#FF6E4C] hover:underline" style={{ fontWeight: 600 }}>
                    Resend verification email
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-6">
            <img src={dvsLogo} alt="DVA Logo" className="h-24 w-auto" />
          </div>
          <h1
            className="text-3xl mb-2"
            style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
          >
            Create Your DVA Account
          </h1>
          <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
            Register your organization and set up your admin account
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2">
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep === 1
                  ? 'bg-[#18365E] text-white'
                  : 'bg-green-500 text-white'
                }`}
              style={{ fontWeight: 600 }}
            >
              {currentStep === 1 ? '1' : <CheckCircle2 className="w-6 h-6" />}
            </div>
            <div
              className={`h-1 w-24 ${currentStep === 2 ? 'bg-[#18365E]' : 'bg-gray-300'
                }`}
            />
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep === 2
                  ? 'bg-[#18365E] text-white'
                  : 'bg-gray-300 text-gray-600'
                }`}
              style={{ fontWeight: 600 }}
            >
              2
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 mt-2">
            <span className="text-sm" style={{ fontWeight: 600, color: '#18365E' }}>
              Organization
            </span>
            <span className="w-24 text-center" />
            <span
              className={`text-sm ${currentStep === 2 ? 'text-[#18365E]' : 'text-[#717182]'
                }`}
              style={{ fontWeight: 600 }}
            >
              Your Account
            </span>
          </div>
        </div>

        {/* Signup Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700" style={{ fontWeight: 500 }}>
                {error}
              </p>
            </div>
          )}

          {/* Step 1: Organization Information */}
          {currentStep === 1 && (
            <form onSubmit={handleNextStep} className="space-y-5">
              <h2
                className="text-xl mb-4"
                style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
              >
                Organization Information
              </h2>

              {/* Organization Name */}
              <div>
                <label
                  htmlFor="client_name"
                  className="block text-sm mb-2 text-[#18365E]"
                  style={{ fontWeight: 600 }}
                >
                  Organization Name *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                  <input
                    id="client_name"
                    type="text"
                    value={clientData.client_name}
                    onChange={(e) =>
                      setClientData({ ...clientData, client_name: e.target.value })
                    }
                    placeholder="e.g., Mercy Health System"
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                    style={{ fontWeight: 500 }}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Organization Type */}
              <div>
                <label
                  htmlFor="client_type"
                  className="block text-sm mb-2 text-[#18365E]"
                  style={{ fontWeight: 600 }}
                >
                  Organization Type *
                </label>
                <select
                  id="client_type"
                  value={clientData.client_type}
                  onChange={(e) =>
                    setClientData({
                      ...clientData,
                      client_type: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                  style={{ fontWeight: 500 }}
                  disabled={loading}
                >
                  {clientTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Organization Address */}
              <div className="space-y-3">
                <label className="block text-sm text-[#18365E]" style={{ fontWeight: 600 }}>
                  Organization Address *
                </label>

                {/* Street Address */}
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                  <input
                    id="street"
                    type="text"
                    value={clientData.address.street}
                    onChange={(e) =>
                      setClientData({
                        ...clientData,
                        address: { ...clientData.address, street: e.target.value },
                      })
                    }
                    placeholder="Street Address"
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                    style={{ fontWeight: 500 }}
                    disabled={loading}
                  />
                </div>

                {/* City and State Row */}
                <div className="grid grid-cols-2 gap-3">
                  {/* City */}
                  <input
                    id="city"
                    type="text"
                    value={clientData.address.city}
                    onChange={(e) =>
                      setClientData({
                        ...clientData,
                        address: { ...clientData.address, city: e.target.value },
                      })
                    }
                    placeholder="City"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                    style={{ fontWeight: 500 }}
                    disabled={loading}
                  />

                  {/* State */}
                  <select
                    id="state"
                    value={clientData.address.state}
                    onChange={(e) =>
                      setClientData({
                        ...clientData,
                        address: { ...clientData.address, state: e.target.value },
                      })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                    style={{ fontWeight: 500 }}
                    disabled={loading}
                  >
                    <option value="">State</option>
                    {US_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ZIP Code */}
                <input
                  id="zip"
                  type="text"
                  value={clientData.address.zip}
                  onChange={(e) =>
                    setClientData({
                      ...clientData,
                      address: { ...clientData.address, zip: e.target.value },
                    })
                  }
                  placeholder="ZIP Code"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                  style={{ fontWeight: 500 }}
                  disabled={loading}
                />
              </div>

              {/* Service Area States */}
              <div>
                <label
                  htmlFor="service_area_states"
                  className="block text-sm mb-2 text-[#18365E]"
                  style={{ fontWeight: 600 }}
                >
                  Service Area States *
                </label>
                <div className="space-y-2">
                  <select
                    id="service_area_states"
                    onChange={(e) => {
                      const state = e.target.value;
                      if (state && !clientData.service_area_states.includes(state)) {
                        setClientData({
                          ...clientData,
                          service_area_states: [...clientData.service_area_states, state],
                        });
                      }
                      e.target.value = '';
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                    style={{ fontWeight: 500 }}
                    disabled={loading}
                  >
                    <option value="">Select states...</option>
                    {US_STATES.filter(state => !clientData.service_area_states.includes(state)).map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>

                  {/* Selected States Pills */}
                  {clientData.service_area_states.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {clientData.service_area_states.map((state) => (
                        <span
                          key={state}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-[#18365E] text-white rounded-full text-sm"
                          style={{ fontWeight: 500 }}
                        >
                          {state}
                          <button
                            type="button"
                            onClick={() =>
                              setClientData({
                                ...clientData,
                                service_area_states: clientData.service_area_states.filter(
                                  (s) => s !== state
                                ),
                              })
                            }
                            className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                            disabled={loading}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Number of Beds */}
              <div>
                <label
                  htmlFor="beds"
                  className="block text-sm mb-2 text-[#18365E]"
                  style={{ fontWeight: 600 }}
                >
                  Number of Beds
                </label>
                <div className="relative">
                  <Bed className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                  <input
                    id="beds"
                    type="number"
                    min="0"
                    value={clientData.beds}
                    onChange={(e) =>
                      setClientData({ ...clientData, beds: e.target.value })
                    }
                    placeholder="0"
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                    style={{ fontWeight: 500 }}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Lives Under VBC Agreements */}
              <div>
                <label
                  htmlFor="lives_under_vbc_agreements"
                  className="block text-sm mb-2 text-[#18365E]"
                  style={{ fontWeight: 600 }}
                >
                  Lives Under Value-Based Care Agreements
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                  <input
                    id="lives_under_vbc_agreements"
                    type="number"
                    min="0"
                    value={clientData.lives_under_vbc_agreements}
                    onChange={(e) =>
                      setClientData({
                        ...clientData,
                        lives_under_vbc_agreements: e.target.value,
                      })
                    }
                    placeholder="0"
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                    style={{ fontWeight: 500 }}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Next Button */}
              <button
                type="submit"
                disabled={loading || !isStep1Valid()}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#18365E] to-[#18365E]/90 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontWeight: 600 }}
              >
                Next: Your Account
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>
          )}

          {/* Step 2: User Information */}
          {currentStep === 2 && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <h2
                className="text-xl mb-4"
                style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
              >
                Your Account Information
              </h2>

              {/* First Name */}
              <div>
                <label
                  htmlFor="name_first"
                  className="block text-sm mb-2 text-[#18365E]"
                  style={{ fontWeight: 600 }}
                >
                  First Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                  <input
                    id="name_first"
                    type="text"
                    value={userData.name_first}
                    onChange={(e) => setUserData({ ...userData, name_first: e.target.value })}
                    placeholder="John"
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                    style={{ fontWeight: 500 }}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Last Name */}
              <div>
                <label
                  htmlFor="name_last"
                  className="block text-sm mb-2 text-[#18365E]"
                  style={{ fontWeight: 600 }}
                >
                  Last Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                  <input
                    id="name_last"
                    type="text"
                    value={userData.name_last}
                    onChange={(e) => setUserData({ ...userData, name_last: e.target.value })}
                    placeholder="Doe"
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                    style={{ fontWeight: 500 }}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm mb-2 text-[#18365E]"
                  style={{ fontWeight: 600 }}
                >
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                  <input
                    id="email"
                    type="email"
                    value={userData.email}
                    onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                    placeholder="you@example.com"
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                    style={{ fontWeight: 500 }}
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
                <p className="mt-1 text-xs text-[#717182]" style={{ fontWeight: 500 }}>
                  We'll send a verification link to this email address
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#18365E] text-[#18365E] rounded-lg hover:bg-[#18365E]/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontWeight: 600 }}
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>

                <button
                  type="submit"
                  disabled={loading || !isStep2Valid()}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#18365E] to-[#18365E]/90 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontWeight: 600 }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Sign In Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-[#FF6E4C] hover:underline"
                style={{ fontWeight: 600 }}
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-[#717182]" style={{ fontWeight: 500 }}>
            By signing up, you agree to our{' '}
            <a href="#" className="text-[#18365E] hover:underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="text-[#18365E] hover:underline">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
